import React from "react"
import { Box, makeStyles, Typography } from "@material-ui/core"
import { FC } from "react"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { DiscountType, IMenuProduct, OfferDisplayLabelType } from "shared/Types/appTypes"
import { TagRounded } from "./Tag"
import { toDinero } from "shared/Helpers/CurrencyHelper"
import { useMoney } from "shared/Modules/Localization/useMoney"

type OfferTagProps = StyleProps & {
    product: IMenuProduct
    size?: "sm" | "default"
}

type StyleProps = Readonly<{
    top: string
    right: string
}>

const useStyles = makeStyles(() => ({
    container: ({ top, right }: StyleProps) => ({
        position: "absolute",
        top,
        right,
    }),
    smTypography: {
        fontSize: "0.625rem",
        lineHeight: "150%",
        letterSpacing: "0.025rem",
    },
}))

export const OfferTag: FC<OfferTagProps> = ({ product, top, right, size = "default" }) => {
    const classes = useStyles({ top, right })
    const moneyFactory = useMoney()

    const { offerDetails } = product
    if (!offerDetails) return null

    const { discount } = offerDetails

    if (!discount || discount.displayLabelType === OfferDisplayLabelType.OLD_NEW_PRICE) {
        return null
    }

    const typographyProps =
        size === "sm" ? { variant: "body1" as const, className: classes.smTypography } : { variant: "body1" as const }

    const getTagText = () => {
        switch (discount.displayLabelType) {
            case OfferDisplayLabelType.SAVE_X:
                if (discount.type === DiscountType.DISCOUNT_PERCENTAGE) {
                    return (
                        <LocalizedStrict id="offer-tag-save-x-percentage" vars={{ amount: discount.percentage.amount }}>
                            <Typography {...typographyProps}>{`Save ${discount.percentage.amount}%`}</Typography>
                        </LocalizedStrict>
                    )
                } else if (discount.type === DiscountType.DISCOUNT_PRICE) {
                    const rawAmount = discount.discountAmount
                        ? moneyFactory.formatFloat(toDinero(discount.discountAmount))
                        : "0"

                    return (
                        <LocalizedStrict id="offer-tag-save-x-price" vars={{ amount: rawAmount }}>
                            <Typography {...typographyProps}>`Save ${rawAmount} kr`</Typography>
                        </LocalizedStrict>
                    )
                }
                return null
            case OfferDisplayLabelType.GOOD_PRICE:
                return (
                    <LocalizedStrict id="offer-tag-good-price">
                        <Typography {...typographyProps}>Good price</Typography>
                    </LocalizedStrict>
                )
            case OfferDisplayLabelType.GREAT_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-great-offer">
                        <Typography {...typographyProps}>Great offer</Typography>
                    </LocalizedStrict>
                )
            case OfferDisplayLabelType.BUY_NOW:
                return (
                    <LocalizedStrict id="offer-tag-buy-now">
                        <Typography {...typographyProps}>Buy now</Typography>
                    </LocalizedStrict>
                )
            case OfferDisplayLabelType.DISCOUNT:
                return (
                    <LocalizedStrict id="offer-tag-discount">
                        <Typography {...typographyProps}>Discount</Typography>
                    </LocalizedStrict>
                )
            case OfferDisplayLabelType.WEEK_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-week-offer">
                        <Typography {...typographyProps}>Week's offer</Typography>
                    </LocalizedStrict>
                )
            case OfferDisplayLabelType.DAY_OFFER:
                return (
                    <LocalizedStrict id="offer-tag-day-offer">
                        <Typography {...typographyProps}>Today's offer</Typography>
                    </LocalizedStrict>
                )
            default:
                return null
        }
    }

    const tagText = getTagText()

    if (!tagText) return null

    return (
        <Box className={classes.container}>
            <TagRounded size={size}>{tagText}</TagRounded>
        </Box>
    )
}
